import { HubSpotFetchType, HubSpotGetObjectsConfig } from 'ihub/common';
import { AdvancedTourStep } from '../shared-types';
import { TaskTourProps } from './task-tour-props';
import { getIfRuleTourSteps } from '../component-tours';

export function getHubspotGetObjectsTourSteps({
	defaultStyles,
	microTaskConfigType,
}: Readonly<TaskTourProps>): AdvancedTourStep[] {
	if (!microTaskConfigType) return [];

	const config = microTaskConfigType as HubSpotGetObjectsConfig;

	const propertySteps: AdvancedTourStep[] = [
		{
			id: 'property-fields',
			selector: '[data-tour="property-fields"]',
			content:
				'The collection of fields that will be selected from the selected object type. If no fields are specifically selected the entire object will be retrieved.',
		},
		{
			id: 'add-property',
			selector: '[data-tour="add-property"]',
			content:
				'Adds a configuration item that will specify a field to select. Note the dropdown list will be populated based on the selected field.',
		},
		...(config.fetchType !== HubSpotFetchType.WhereFilter
			? [
					{
						id: 'association-fields',
						selector: '[data-tour="association-fields"]',
						content:
							'The collection of fields that will be selected from the selected object type. If no fields are specifically selected the entire object will be retrieved.',
					},
					{
						id: 'add-property',
						selector: '[data-tour="add-association"]',
						content:
							'Adds a configuration item that will specify a field to select. Note the dropdown list will be populated based on the selected field.',
					},
				]
			: []),
	];

	const commonSteps: AdvancedTourStep[] = [
		{
			id: 'error-when-empty',
			selector: '[data-tour="error-when-empty"]',
			content:
				'When checked and no objects with the specified conditions have been located, the task will fail with the error message.',
		},
		{
			id: 'enable-retry',
			selector: '[data-tour="enable-retry"]',
			content: 'When checked, the user will be able to configure the retry configurables.',
		},
		...(config.enableRetries
			? [
					{
						id: 'retry-limit',
						selector: '[data-tour="retry-limit"]',
						content: 'The amount of times the task will retry to get the objects.',
					},
					{
						id: 'retry-delay',
						selector: '[data-tour="retry-delay"]',
						content: 'A delay time in ms. This will set the time to wait between retries.',
					},
				]
			: []),
	];

	const fetchByPageSteps: AdvancedTourStep[] = [
		{
			id: 'fetch-by-page',
			selector: '[data-tour="fetch-by-page"]',
			content: 'When checked, the user will be able to configure the pagination configurables.',
		},
		...(config.fetchByPage
			? [
					{
						id: 'page-offset',
						selector: '[data-tour="page-offset"]',
						content: 'This should be a variable that increases by the page size.',
					},
					{
						id: 'page-token',
						selector: '[data-tour="page-token"]',
						content: 'The page token that would be used for fetching objects.',
					},
				]
			: []),
	];

	const fetchTypeSteps = (): AdvancedTourStep[] => {
		switch (config.fetchType) {
			case HubSpotFetchType.GetAssociationsById:
				return [
					{
						id: 'hubspot-object-id',
						selector: '[data-tour="hubspot-object-id"]',
						content:
							'Required: The HubSpot Id (or other unique identifier - change Custom Id Property) of the object whose associations will be retrieved.',
					},
					{
						id: 'custom-id',
						selector: '[data-tour="custom-id"]',
						content:
							'Required: The unique Id field name that should be used for the retrieval request.',
					},
					...commonSteps,
					...propertySteps,
				];

			case HubSpotFetchType.GetAssociations:
				return [
					{
						id: 'fetch-from-archive',
						selector: '[data-tour="fetch-from-archive"]',
						content:
							'When checked, the objects will be retrieved even if they are archived in HubSpot.',
					},
					...fetchByPageSteps,
					...commonSteps,
					...propertySteps,
				];

			case HubSpotFetchType.WhereFilter:
				return [
					...fetchByPageSteps,
					...commonSteps,
					...propertySteps,
					...getIfRuleTourSteps({
						defaultStyles,
						hasIfRules: config.ruleGroup.ruleGroup.length > 0,
					}),
				];

			default:
				return [];
		}
	};

	const hasAccessToken = config.accessToken?.trim().length > 0;

	return [
		{
			id: 'use-variable-token',
			selector: '[data-tour="use-variable-token"]',
			content:
				'This option changes the Access Token input behavior to allow free text or expression inputs.',
		},
		{
			id: 'access-token',
			selector: '[data-tour="access-token"]',
			content: 'Required: The HubSpot access token that will be used to validate the request.',
		},
		...(hasAccessToken
			? [
					{
						id: 'object-type',
						selector: '[data-tour="object-type"]',
						content:
							'Required: Once a valid HubSpot access token is provided the task will fetch a list of available objects from the HubSpot environment.',
					},
					{
						id: 'fetch-type',
						selector: '[data-tour="fetch-type"]',
						content: 'Required: The type of fetch that should be executed when the task runs.',
					},
					...fetchTypeSteps(),
				]
			: []),
	].map(step => ({ ...step, styles: defaultStyles }));
}
